import { logger } from '../utils/logger';
import { API_BASE_URL } from '../config/apiConfig';

export interface Client {
  id: number;
  name: string;
  email: string;
  phone?: string;
  company?: string;
  status: 'active' | 'inactive' | 'suspended';
  totalProjects: number;
  activeProjects: number;
  totalSpent: number;
  lastLogin?: string;
  created_at: string;
  updated_at: string;
  address?: string;
  website?: string;
  industry?: string;
  primaryContact?: string;
  notes?: string;
}

export interface ClientStats {
  total: number;
  active: number;
  inactive: number;
  suspended: number;
  totalRevenue: number;
  averageProjectValue: number;
  lastUpdated?: string;
}

export interface ClientProject {
  id: number;
  name: string;
  status: 'planning' | 'active' | 'on_hold' | 'completed' | 'cancelled' | 'in_progress';
  progress: number;
  budget: number;
  spent: number;
  startDate: string;
  endDate?: string;
  projectManager?: string;
  developer?: string;
  // Task statistics
  taskCount?: number;
  completedTasks?: number;
  totalHours?: number;
  totalStoryPoints?: number;
  hourlyRate?: number;
  projectEstimate?: number;
}

export interface ClientsResponse {
  clients: Client[];
  pagination: {
    current_page: number;
    per_page: number;
    total: number;
    last_page: number;
  };
}

export interface ClientProjectsResponse {
  projects: ClientProject[];
  total: number;
}

class ClientService {
  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('auth_token');
    return {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  }

  async getClientStats(): Promise<ClientStats> {
    try {
      logger.info('Fetching client statistics');
      
      const response = await fetch(`${API_BASE_URL}/admin/clients/stats`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'Failed to fetch client stats');
      }

      logger.info('Client statistics fetched successfully', { stats: data.data });
      return data.data;
    } catch (error) {
      logger.error('Failed to fetch client statistics', { error });
      throw error;
    }
  }

  async getClients(params?: {
    search?: string;
    status?: string;
    page?: number;
    per_page?: number;
  }): Promise<ClientsResponse> {
    try {
      logger.info('Fetching clients', { params });
      
      const queryParams = new URLSearchParams();
      if (params?.search) queryParams.set('search', params.search);
      if (params?.status && params.status !== 'all') queryParams.set('status', params.status);
      if (params?.page) queryParams.set('page', params.page.toString());
      if (params?.per_page) queryParams.set('per_page', params.per_page.toString());

      const url = `${API_BASE_URL}/admin/clients${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'Failed to fetch clients');
      }

      logger.info('Clients fetched successfully', { 
        count: data.data.clients.length,
        total: data.data.pagination.total 
      });
      return data.data;
    } catch (error) {
      logger.error('Failed to fetch clients', { error, params });
      throw error;
    }
  }

  async getClient(id: number): Promise<Client> {
    try {
      logger.info('Fetching client details', { clientId: id });
      
      const response = await fetch(`${API_BASE_URL}/admin/clients/${id}`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'Failed to fetch client');
      }

      logger.info('Client details fetched successfully', { client: data.data });
      return data.data;
    } catch (error) {
      logger.error('Failed to fetch client details', { error, clientId: id });
      throw error;
    }
  }

  async createClient(clientData: Omit<Client, 'id' | 'totalProjects' | 'activeProjects' | 'totalSpent' | 'created_at' | 'updated_at' | 'lastLogin'>): Promise<Client> {
    try {
      logger.info('Creating new client', { clientData: { ...clientData, email: '[REDACTED]' } });
      
      const response = await fetch(`${API_BASE_URL}/admin/clients`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(clientData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'Failed to create client');
      }

      logger.info('Client created successfully', { client: data.data });
      return data.data;
    } catch (error) {
      logger.error('Failed to create client', { error, clientData: { ...clientData, email: '[REDACTED]' } });
      throw error;
    }
  }

  async updateClient(id: number, clientData: Partial<Client>): Promise<Client> {
    try {
      logger.info('Updating client', { clientId: id, updateData: { ...clientData, email: clientData.email ? '[REDACTED]' : undefined } });
      
      const response = await fetch(`${API_BASE_URL}/admin/clients/${id}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(clientData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'Failed to update client');
      }

      logger.info('Client updated successfully', { client: data.data });
      return data.data;
    } catch (error) {
      logger.error('Failed to update client', { error, clientId: id });
      throw error;
    }
  }

  async deleteClient(id: number): Promise<void> {
    try {
      logger.info('Deleting client', { clientId: id });
      
      const response = await fetch(`${API_BASE_URL}/admin/clients/${id}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'Failed to delete client');
      }

      logger.info('Client deleted successfully', { clientId: id });
    } catch (error) {
      logger.error('Failed to delete client', { error, clientId: id });
      throw error;
    }
  }

  async changeClientStatus(id: number, status: 'active' | 'inactive' | 'suspended'): Promise<Client> {
    try {
      logger.info('Changing client status', { clientId: id, newStatus: status });
      
      const response = await fetch(`${API_BASE_URL}/admin/clients/${id}/status`, {
        method: 'PATCH',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ status }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'Failed to change client status');
      }

      logger.info('Client status changed successfully', { client: data.data });
      return data.data;
    } catch (error) {
      logger.error('Failed to change client status', { error, clientId: id, status });
      throw error;
    }
  }

  async getClientProjects(clientId: number): Promise<ClientProjectsResponse> {
    try {
      logger.info('Fetching client projects', { clientId });
      console.log('DEBUG: Fetching projects for client ID:', clientId);
      console.log('DEBUG: API URL:', `${API_BASE_URL}/admin/clients/${clientId}/projects`);
      
      const headers = this.getAuthHeaders();
      console.log('DEBUG: Auth headers:', headers);
      
      const response = await fetch(`${API_BASE_URL}/admin/clients/${clientId}/projects`, {
        method: 'GET',
        headers,
      });

      console.log('DEBUG: Response status:', response.status);
      console.log('DEBUG: Response headers:', response.headers);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('DEBUG: Error response:', errorText);
        throw new Error(`HTTP error! status: ${response.status}, response: ${errorText}`);
      }

      const data = await response.json();
      console.log('DEBUG: Response data:', data);
      
      if (!data.success) {
        throw new Error(data.message || 'Failed to fetch client projects');
      }

      logger.info('Client projects fetched successfully', { 
        clientId,
        projectCount: data.data.projects.length 
      });
      return data.data;
    } catch (error) {
      console.error('DEBUG: Exception in getClientProjects:', error);
      logger.error('Failed to fetch client projects', { error, clientId });
      throw error;
    }
  }

  async getClientCommunications(clientId: number): Promise<any> {
    try {
      logger.info('Fetching client communications', { clientId });
      
      const response = await fetch(`${API_BASE_URL}/admin/clients/${clientId}/communications`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'Failed to fetch client communications');
      }

      logger.info('Client communications fetched successfully', { 
        clientId,
        communicationCount: data.data.communications.length 
      });
      return data.data;
    } catch (error) {
      logger.error('Failed to fetch client communications', { error, clientId });
      throw error;
    }
  }

  async getClientBilling(clientId: number): Promise<any> {
    try {
      logger.info('Fetching client billing', { clientId });
      
      const response = await fetch(`${API_BASE_URL}/admin/clients/${clientId}/billing`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'Failed to fetch client billing');
      }

      logger.info('Client billing fetched successfully', { 
        clientId,
        totalBudget: data.data.summary?.totalBudget || 0
      });
      return data.data;
    } catch (error) {
      logger.error('Failed to fetch client billing', { error, clientId });
      throw error;
    }
  }

  async getClientContacts(clientId: number): Promise<any> {
    try {
      logger.info('Fetching client contacts', { clientId });
      
      const response = await fetch(`${API_BASE_URL}/admin/clients/${clientId}/contacts`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'Failed to fetch client contacts');
      }

      logger.info('Client contacts fetched successfully', { 
        clientId,
        contactCount: data.data.contacts.length 
      });
      return data.data;
    } catch (error) {
      logger.error('Failed to fetch client contacts', { error, clientId });
      throw error;
    }
  }
}

export const clientService = new ClientService();